import { browserTimeZone } from '../config';

export function todayInDateStr(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(new Date());
  }
}

export function addDaysToDateStr(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function dateStrToLocalDate(dateStr: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function localDateToDateStr(date: Date): string {
  return new Intl.DateTimeFormat('en-CA').format(date);
}

export function atMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfWeek(date: Date): Date {
  const d = atMidnight(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

export function endOfWeek(date: Date): Date {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return d;
}

export function isFullWeekWithinRange(date: Date, from: Date | null, to: Date | null): boolean {
  if (!from || !to) return false;
  const weekStart = startOfWeek(date);
  const weekEnd = endOfWeek(date);
  return weekStart.getTime() >= atMidnight(from).getTime() && weekEnd.getTime() <= atMidnight(to).getTime();
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return atMidnight(a).getTime() === atMidnight(b).getTime();
}

export function rangeDayClassName(date: Date, from: Date | null, to: Date | null): string {
  if (!from || !to) return '';
  if (isFullWeekWithinRange(date, from, to)) return 'shows-week-band';
  if (isSameCalendarDay(date, from) || isSameCalendarDay(date, to)) return '';
  return 'shows-suppress-inrange';
}

export function dateStrDayOfWeek(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00.000Z`).getUTCDay();
}

/** 0=Sun..6=Sat. Mirrors the backend's own weekend-window rule exactly:
 *  Sun -> just today; Sat -> today+tomorrow; Fri -> today..Sunday;
 *  Mon-Thu -> the UPCOMING Friday..Sunday (not starting from today). */
export function computeWeekendWindow(todayStr: string): { start: string; end: string } {
  const dow = dateStrDayOfWeek(todayStr);
  if (dow === 0) return { start: todayStr, end: todayStr };
  if (dow === 6) return { start: todayStr, end: addDaysToDateStr(todayStr, 1) };
  if (dow === 5) return { start: todayStr, end: addDaysToDateStr(todayStr, 2) };
  const toFriday = 5 - dow;
  return { start: addDaysToDateStr(todayStr, toFriday), end: addDaysToDateStr(todayStr, toFriday + 2) };
}

const CHIP_WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CHIP_MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatChipDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  return `${CHIP_WEEKDAY_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${CHIP_MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * Computes the effective target booking date (YYYY-MM-DD) from URL query parameters.
 * When `dateFrom` is set, returns `dateFrom`.
 * When `today` is set, returns today's local date.
 * When `weekend` is set, returns the upcoming weekend start date.
 */
export function getEffectiveBookingDate(
  searchParams: { get: (key: string) => string | null } | URLSearchParams,
): string | null {
  const dateFrom = searchParams.get('dateFrom');
  if (dateFrom) return dateFrom;
  const tz = browserTimeZone();
  if (searchParams.get('today') === 'true') {
    return todayInDateStr(tz);
  }
  if (searchParams.get('weekend') === 'true') {
    return computeWeekendWindow(todayInDateStr(tz)).start;
  }
  return null;
}
